import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetCompanyQuery } from "../../../../store/Company/companyApiSlice";
import { selectActiveCompany } from "../../../../store/activeCompanySlice";
import {
  buildWebProfileSummary,
  PLAN_YEARLY,
} from "../../../../constants/webProfilePricing";
import { CommonToast } from "../../../common/Toast";
import {
  actingStatusOf,
  canJoinMembership,
  continueTarget,
  effectiveModeOf,
  introCopy,
  isMemberStatus,
  resolveTargetCompanyId,
  showsUpsell,
} from "./planFields";
import { summaryCompanyName } from "./paymentDetailsFields";

/* Step 1 of the checkout, transcribed from PaymentPlans.jsx.
 *
 * Same two company queries, same four effects, same TIP-530 guard, same handoff
 * to the payment step. `blocked` replaces the live `if (!targetCompanyId)
 * return null`.
 */
export const usePlanSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useSelector((s) => s.auth);
  const activeCompany = useSelector(selectActiveCompany);

  const targetCompanyId = resolveTargetCompanyId(location.state);

  const { data: company } = useGetCompanyQuery(
    { token, companyId: targetCompanyId },
    { skip: !targetCompanyId }
  );

  /* TIP-493 (W3) — the cached active company's profile_status can be stale
     (right after buying membership, say), so the acting company's current
     status is fetched and preferred. */
  const activeCompanyId = activeCompany?.companyID;
  const { data: actingCompany } = useGetCompanyQuery(
    { token, companyId: activeCompanyId },
    { skip: !activeCompanyId }
  );
  const isMember = isMemberStatus(actingStatusOf(actingCompany, activeCompany));
  /* Membership attaches to a business, so an account that has claimed none
     cannot buy the bundle — the card is shown greyed rather than hidden. */
  const canJoin = canJoinMembership(activeCompanyId);

  const [mode, setMode] = useState(isMember ? "member" : "payg");
  const [plan, setPlan] = useState(PLAN_YEARLY);

  useEffect(() => {
    if (isMember) setMode("member");
  }, [isMember]);

  /* The acting company resolves after the first render, so the bundle can already be selected — by the summary panel's upsell, or by a term toggle — by the time... */
  useEffect(() => {
    if (!canJoin && mode === "bundle") setMode("payg");
  }, [canJoin, mode]);

  useEffect(() => {
    window.scrollTo(0, 0);
    /* A fresh checkout drops any stale mode flag, so the payment step cannot
       inherit a member or bundle choice across accounts (N2). */
    localStorage.removeItem("webProfilePurchaseMode");
    if (targetCompanyId) {
      localStorage.setItem("payasyougoCompanyID", JSON.stringify(targetCompanyId));
    }
  }, [targetCompanyId]);

  /* TIP-530 — without a target company the checkout could take a real payment
     against a null companyID. Direct entry has no context, so it goes to
     search instead. */
  useEffect(() => {
    if (!targetCompanyId) {
      CommonToast.error("Select a business to buy its Web Profile.");
      navigate("/search", { replace: true });
    }
  }, [targetCompanyId, navigate]);

  const effectiveMode = effectiveModeOf(isMember, mode, canJoin);

  const handleContinue = () => {
    const target = continueTarget({
      token,
      mode: effectiveMode,
      plan,
      companyId: targetCompanyId,
    });
    if (target.remember) localStorage.setItem("lastVisited", target.remember);
    navigate(target.to, target.options);
  };

  return {
    blocked: !targetCompanyId,
    companyId: targetCompanyId,
    company,
    /* Same resolution as the order panel, so the heading and the panel cannot
       disagree — and so a LISTING, whose record the TREVEX lookup cannot
       find, is still named. */
    companyName: summaryCompanyName(company),
    isMember,
    canJoin,
    mode,
    setMode,
    plan,
    /* Picking a term also picks the bundle — the terms only exist inside it. */
    selectBundlePlan: (nextPlan) => {
      setMode("bundle");
      setPlan(nextPlan);
    },
    effectiveMode,
    /* Ex-VAT (TIP-646): no billing address exists on this step, so no tax
       figure can honestly be shown. The panel says VAT is added at checkout,
       and step 2 overlays Stripe's own quote. */
    summary: buildWebProfileSummary({ mode: effectiveMode, plan }),
    intro: introCopy(isMember, canJoin),
    upsell: showsUpsell(isMember, mode, canJoin),
    handleContinue,
    handleCancel: () => navigate(-1),
  };
};

export default usePlanSelection;
